// Slimmere suggesties: waardering per activiteit op basis van wat je recent deed, favorieten,
// weer, tijdstip en beschikbare tijd. Puur (geen React/opslag), `now` en `random` zijn injecteerbaar.
import { getMainCategory } from './activities';
import type { Activity, LogEntry, WeatherState } from './types';

/** Beschikbare tijd in minuten (maximum). null = geen limiet. */
export type TimeBudget = 15 | 30 | 60 | 120 | null;
export const TIME_BUDGET_OPTIONS: ReadonlyArray<{ value: Exclude<TimeBudget, null>; label: string }> = [
  { value: 15, label: '15 min' },
  { value: 30, label: '30 min' },
  { value: 60, label: '1 uur' },
  { value: 120, label: '2 uur' },
];

export interface SuggestContext {
  now: Date;
  /** Volledig logboek (nieuwste of oudste eerst, volgorde doet er niet toe). */
  logs: readonly LogEntry[];
  /** id's van favoriete activiteiten. */
  favorites: ReadonlySet<number>;
  weather: Pick<WeatherState, 'kind' | 'isBadWeather' | 'source'>;
  /** Net getoonde id's: krijgen een lichte malus zodat "Nieuwe suggesties" echt anders is. */
  recentlyShownIds?: ReadonlySet<number>;
  /** Beschikbare tijd; als gezet krijgt elke kaart de reden "Past in je tijd" (lage prioriteit). */
  timeBudget?: TimeBudget;
  /** Standaard Math.random. */
  random?: () => number;
}

export interface Suggestion {
  activity: Activity;
  /** Totaalscore (hoger = beter), alleen voor tests/debuggen. */
  score: number;
  /**
   * Korte Nederlandse redenen voor op de kaart, maximaal 2, meest relevante eerst. Voorbeelden:
   * "Favoriet", "Lang niet gedaan", "Nog niet geprobeerd", "Lekker weer voor buiten",
   * "Past bij het weer", "Rustig voor de avond", "Past in je tijd".
   */
  reasons: string[];
}

/** Activiteiten die in de beschikbare tijd passen (duur <= budget). null = alles. */
export function filterByTime(list: Activity[], budget: TimeBudget): Activity[] {
  if (budget === null) return [...list];
  return list.filter((a) => {
    const d = Number(a.duur);
    return Number.isFinite(d) && d <= budget;
  });
}

/** Gewichten (punten). Het toeval telt 0..RANDOM_SPREAD mee. */
export const WEIGHTS = {
  today: -60,
  yesterday: -12,
  twoDaysAgo: -6,
  neglected: 10,
  neverPlayed: 12,
  favorite: 12,
  weatherFit: 8,
  weatherMismatch: -10,
  eveningQuiet: 6,
  eveningEnergetic: -8,
  recentlyShown: -5,
  timeFit: 1,
  randomSpread: 8,
} as const;

const NEGLECTED_DAYS = 14;

function dayIndex(d: Date): number {
  return Math.floor((d.getTime() - d.getTimezoneOffset() * 60000) / 86400000);
}

function norm(s: unknown): string {
  return typeof s === 'string' ? s.toLowerCase().trim() : '';
}

/** Per activiteit-id en per naam: aantal dagen geleden dat het voor het laatst gedaan is. */
function lastPlayed(logs: readonly LogEntry[], now: Date): { byId: Map<number, number>; byName: Map<string, number> } {
  const byId = new Map<number, number>();
  const byName = new Map<string, number>();
  const today = dayIndex(now);
  for (const l of logs) {
    if (!l || typeof l !== 'object') continue;
    const t = new Date(l.moment);
    if (Number.isNaN(t.getTime())) continue;
    const ago = Math.max(0, today - dayIndex(t));
    if (typeof l.activityId === 'number' && Number.isFinite(l.activityId)) {
      byId.set(l.activityId, Math.min(byId.get(l.activityId) ?? Infinity, ago));
    }
    const n = norm(l.naam);
    if (n) byName.set(n, Math.min(byName.get(n) ?? Infinity, ago));
  }
  return { byId, byName };
}

function scoreAll(candidates: Activity[], ctx: SuggestContext): Suggestion[] {
  const random = ctx.random ?? Math.random;
  const { byId, byName } = lastPlayed(ctx.logs ?? [], ctx.now);
  const hour = ctx.now.getHours();
  const evening = hour >= 19 || hour < 7;
  const w = ctx.weather;
  const weatherKnown = !!w && w.source !== 'onbekend' && w.kind !== 'onbekend';

  return candidates.map((a) => {
    let score = 0;
    const reasons: string[] = [];
    const ago = Math.min(byId.get(a.id) ?? Infinity, byName.get(norm(a.naam)) ?? Infinity);

    if (ago === 0) score += WEIGHTS.today;
    else if (ago === 1) score += WEIGHTS.yesterday;
    else if (ago === 2) score += WEIGHTS.twoDaysAgo;

    const fav = ctx.favorites.has(a.id);
    if (fav) {
      score += WEIGHTS.favorite;
      reasons.push('Favoriet');
    }
    if (ago === Infinity) {
      score += WEIGHTS.neverPlayed;
      reasons.push('Nog niet geprobeerd');
    } else if (ago > NEGLECTED_DAYS) {
      score += WEIGHTS.neglected;
      reasons.push('Lang niet gedaan');
    }

    if (weatherKnown) {
      if (w.isBadWeather) {
        if (a.type === 'Binnen') {
          score += WEIGHTS.weatherFit;
          reasons.push('Past bij het weer');
        } else if (a.weer === 'Droog') score += WEIGHTS.weatherMismatch;
      } else if (a.type === 'Buiten') {
        score += WEIGHTS.weatherFit;
        reasons.push('Lekker weer voor buiten');
      }
    }

    if (evening) {
      if (a.energie === 'Rustig') {
        score += WEIGHTS.eveningQuiet;
        reasons.push('Rustig voor de avond');
      } else if (a.energie === 'Veel energie') score += WEIGHTS.eveningEnergetic;
    }

    if (ctx.recentlyShownIds?.has(a.id)) score += WEIGHTS.recentlyShown;
    if (ctx.timeBudget != null) {
      score += WEIGHTS.timeFit;
      reasons.push('Past in je tijd');
    }

    const r = random();
    score += (Number.isFinite(r) ? Math.min(Math.max(r, 0), 1) : 0.5) * WEIGHTS.randomSpread;
    return { activity: a, score, reasons: reasons.slice(0, 2) };
  });
}

/**
 * Kiest `count` (standaard 5) suggesties uit `candidates`. Gewogen score met toeval, zodat het
 * niet elke keer dezelfde top is. Regels (gewichten vrij te kiezen, maar gedrag moet kloppen):
 *  - Vandaag al gedaan: sterke malus; gisteren/eergisteren: lichte malus (match op activityId of naam).
 *  - Lang (>14 dagen) niet gedaan, of nog nooit gedaan: bonus ("Lang niet gedaan"/"Nog niet geprobeerd").
 *  - Favoriet: bonus.
 *  - Weer: droog+bekend weer → buitenactiviteit bonus; slecht weer → binnenactiviteit bonus.
 *    Bij source 'onbekend' geen weerbonus of -malus.
 *  - Tijdstip: vanaf 19:00 en vóór 07:00 → 'Rustig' bonus, 'Veel energie' malus.
 *  - recentlyShownIds: lichte malus.
 *  - Variatie: maximaal 3 uit dezelfde hoofdcategorie (getMainCategory) als er genoeg alternatieven zijn.
 *  - Bij <= count kandidaten: geef ze allemaal terug (gesorteerd op score).
 */
export function suggest(candidates: Activity[], ctx: SuggestContext, count = 5): Suggestion[] {
  const n = Math.max(0, Math.floor(count));
  const sorted = scoreAll(candidates, ctx).sort((a, b) => b.score - a.score);
  if (sorted.length <= n) return sorted;

  const MAX_PER_CATEGORY = 3;
  const perCat = new Map<string, number>();
  const picked: Suggestion[] = [];
  const skipped: Suggestion[] = [];
  for (const s of sorted) {
    const cat = getMainCategory(s.activity.type, s.activity.energie, s.activity.categorie);
    const used = perCat.get(cat) ?? 0;
    if (picked.length < n && used < MAX_PER_CATEGORY) {
      picked.push(s);
      perCat.set(cat, used + 1);
    } else skipped.push(s);
  }
  // Te weinig variatie beschikbaar: vul aan met de beste overgebleven.
  for (const s of skipped) {
    if (picked.length >= n) break;
    picked.push(s);
  }
  return picked;
}

/** "Verras me": één activiteit, gekozen uit de beste ~5 met toeval. null als er geen kandidaten zijn. */
export function surpriseMe(candidates: Activity[], ctx: SuggestContext): Suggestion | null {
  const top = suggest(candidates, ctx, 5);
  if (top.length === 0) return null;
  const r = (ctx.random ?? Math.random)();
  const idx = Number.isFinite(r) ? Math.min(top.length - 1, Math.max(0, Math.floor(r * top.length))) : 0;
  return top[idx];
}
